/**
 * Bluetooth Printer Manager
 * Menggunakan Web Bluetooth API untuk koneksi ke printer thermal (ESC/POS)
 */

class BluetoothPrinter {
  constructor() {
    this.device = null;
    this.characteristic = null;
    this.encoder = new TextEncoder();
    this._disconnectHandlers = new Set();
    this._reconnectAttempts = 3;
    this._disconnectAttached = false;
  }

  _attachDisconnectHandler() {
    if (!this.device) return;
    try {
      if (!this._disconnectAttached) {
        this.device.addEventListener(
          "gattserverdisconnected",
          this._onDisconnected.bind(this),
        );
        this._disconnectAttached = true;
      }
    } catch (e) {
      // ignore
    }
  }

  _onDisconnected(ev) {
    console.warn("Bluetooth device disconnected", ev);
    this.characteristic = null;
    this._notifyDisconnect();
    // try auto-reconnect in background
    (async () => {
      for (let i = 0; i < this._reconnectAttempts; i++) {
        try {
          const res = await this.connect({ retries: 1 });
          if (res.success) return;
        } catch (e) {
          // continue
        }
        await new Promise((r) => setTimeout(r, 1000 * (i + 1)));
      }
    })();
  }

  _notifyDisconnect() {
    for (const cb of Array.from(this._disconnectHandlers)) {
      try {
        cb(this.device);
      } catch (e) {
        /* ignore */
      }
    }
  }

  onDisconnected(cb) {
    if (typeof cb === "function") this._disconnectHandlers.add(cb);
  }

  removeDisconnected(cb) {
    this._disconnectHandlers.delete(cb);
  }

  // ESC/POS Commands
  INIT = "\x1B\x40";
  ALIGN_CENTER = "\x1B\x61\x31";
  ALIGN_LEFT = "\x1B\x61\x30";
  BOLD_ON = "\x1B\x21\x08";
  BOLD_OFF = "\x1B\x21\x00";
  LINE_FEED = "\x0A";
  CUT_PAPER = "\x1D\x56\x41\x03";

  isSupported() {
    return "bluetooth" in navigator;
  }

  async autoReconnect() {
    if (!this.isSupported()) return { success: false };
    try {
      if (!navigator.bluetooth.getDevices) return { success: false };
      const devices = await navigator.bluetooth.getDevices();
      if (devices.length > 0) {
        this.device = devices[0];
        if (this.device?.gatt) {
          try {
            const server = await this.device.gatt.connect();
            this.characteristic = await this._findCharacteristic(server);
            this._attachDisconnectHandler();
            return { success: true, deviceName: this.device.name };
          } catch (err) {
            return { success: false, error: err.message };
          }
        }
      }
      return { success: false };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // Known printer service/characteristic UUID pairs to try in order
  PRINTER_PROFILES = [
    // Most common Chinese BT thermal printers (PeriPage, Paperang clones, etc.)
    {
      service: "000018f0-0000-1000-8000-00805f9b34fb",
      characteristic: "00002af1-0000-1000-8000-00805f9b34fb",
    },
    // Xprinter, HOIN, MTP series
    {
      service: "e7810a71-73ae-499d-8c15-faa9aef0c3f2",
      characteristic: "bef8d6c9-9c21-4c9e-b632-bd58c1009f9f",
    },
    // InnerPrinter / some Epson BT models
    {
      service: "49535343-fe7d-4ae5-8fa9-9fafd205e455",
      characteristic: "49535343-8841-43f4-a8d4-ecbe34729bb3",
    },
  ];

  async _findCharacteristic(server) {
    for (const profile of this.PRINTER_PROFILES) {
      try {
        const svc = await server.getPrimaryService(profile.service);
        const chr = await svc.getCharacteristic(profile.characteristic);
        return chr;
      } catch (_) {
        // try next
      }
    }
    // Last resort: enumerate all services and find first writable characteristic
    try {
      const services = await server.getPrimaryServices();
      for (const svc of services) {
        try {
          const chars = await svc.getCharacteristics();
          for (const chr of chars) {
            if (chr.properties.write || chr.properties.writeWithoutResponse) {
              return chr;
            }
          }
        } catch (_) {
          /* continue */
        }
      }
    } catch (_) {
      /* ignore */
    }
    throw new Error("Tidak menemukan karakteristik printer yang kompatibel");
  }

  async connect({ retries = this._reconnectAttempts } = {}) {
    if (!this.isSupported()) {
      return {
        success: false,
        error: "Web Bluetooth tidak didukung di browser ini",
      };
    }
    try {
      if (this.device?.gatt?.connected && this.characteristic) {
        return { success: true, deviceName: this.device.name };
      }
      // GATT still connected but characteristic lost → re-acquire without re-pairing
      if (this.device?.gatt?.connected && !this.characteristic) {
        try {
          const server = await this.device.gatt.connect();
          this.characteristic = await this._findCharacteristic(server);
          this._attachDisconnectHandler();
          return { success: true, deviceName: this.device.name };
        } catch (err) {
          // re-acquisition failed; force disconnect then fall through to gatt.connect retry
          try {
            await this.device.gatt.disconnect();
          } catch (_) {}
        }
      }
      // Re-connect to known device
      if (this.device?.gatt && !this.device.gatt.connected) {
        let lastErr = null;
        for (let i = 0; i <= retries; i++) {
          try {
            const server = await this.device.gatt.connect();
            this.characteristic = await this._findCharacteristic(server);
            this._attachDisconnectHandler();
            return { success: true, deviceName: this.device.name };
          } catch (err) {
            lastErr = err;
            await new Promise((r) => setTimeout(r, 300 * Math.pow(2, i)));
          }
        }
        return { success: false, error: lastErr?.message || "Reconnect gagal" };
      }
      // First-time pair: show only likely printer devices
      const optionalServices = this.PRINTER_PROFILES.map((p) => p.service);
      this.device = await navigator.bluetooth.requestDevice({
        filters: [
          { services: ["000018f0-0000-1000-8000-00805f9b34fb"] },
          { services: ["e7810a71-73ae-499d-8c15-faa9aef0c3f2"] },
          { services: ["49535343-fe7d-4ae5-8fa9-9fafd205e455"] },
          { namePrefix: "MTP" },
          { namePrefix: "MPT" },
          { namePrefix: "BT" },
          { namePrefix: "XP" },
          { namePrefix: "POS" },
          { namePrefix: "RPP" },
          { namePrefix: "HOIN" },
          { namePrefix: "Print" },
          { namePrefix: "print" },
          { namePrefix: "Inner" },
        ],
        optionalServices,
      });
      // After pairing, give the device a moment before connecting
      await new Promise((r) => setTimeout(r, 500));
      let lastErr = null;
      for (let i = 0; i <= 3; i++) {
        try {
          const server = await this.device.gatt.connect();
          this.characteristic = await this._findCharacteristic(server);
          this._attachDisconnectHandler();
          return { success: true, deviceName: this.device.name };
        } catch (err) {
          lastErr = err;
          await new Promise((r) => setTimeout(r, 500 * (i + 1)));
        }
      }
      return { success: false, error: lastErr?.message || "Gagal terhubung" };
    } catch (error) {
      if (
        error.name === "NotFoundError" ||
        error.message?.includes("cancelled")
      ) {
        return { success: false, error: "Pemilihan perangkat dibatalkan" };
      }
      return { success: false, error: error.message || String(error) };
    }
  }

  async disconnect() {
    try {
      if (this.device?.gatt?.connected) {
        await this.device.gatt.disconnect();
      }
    } finally {
      this.characteristic = null;
      this._notifyDisconnect();
    }
  }

  isConnected() {
    return !!(this.device?.gatt?.connected && this.characteristic);
  }

  async sendData(text) {
    if (!this.characteristic) throw new Error("Printer tidak terhubung");
    const data = this.encoder.encode(text);
    const chunkSize = 512;
    for (let i = 0; i < data.length; i += chunkSize) {
      await this.characteristic.writeValue(data.slice(i, i + chunkSize));
      await new Promise((r) => setTimeout(r, 50));
    }
  }

  // Helper: pad left-right columns to 32 chars
  _cols(left, right) {
    const padding = Math.max(0, 32 - left.length - right.length);
    return left + " ".repeat(padding) + right;
  }

  // Helper: word-wrap text to lines of max `width` chars
  _wrap(text, width = 32) {
    const words = String(text).split(" ");
    const lines = [];
    let current = "";
    for (const word of words) {
      if (!current) {
        current = word;
      } else if (current.length + 1 + word.length <= width) {
        current += " " + word;
      } else {
        lines.push(current);
        current = word;
      }
    }
    if (current) lines.push(current);
    return lines.length ? lines : [""];
  }

  /**
   * Print receipt for carwash transaction
   * @param {Object} txData - transaction data
   *   - invoice_number, created_at, cashier_name, customer_name
   *   - plate_number, vehicle_type
   *   - details: [{ item_type, item_name, unit_price, quantity, subtotal }]
   *   - total_amount, discount_amount, paid_amount, payment_method
   * @param {Object} settings - store settings (store_name, store_address, store_phone, receipt_footer)
   */
  async printReceipt(txData, settings = {}) {
    if (!this.isConnected()) throw new Error("Printer tidak terhubung");
    try {
      const storeName = settings.store_name || "Car Wash";
      const storeAddress = settings.store_address || "";
      const storePhone = settings.store_phone || "";
      const footer = settings.receipt_footer || "Terima kasih!";

      const fmtNum = (n) =>
        Number(n || 0).toLocaleString("id-ID", { maximumFractionDigits: 0 });
      const fmtDate = (d) => {
        const dt = new Date(d);
        const date = dt.toLocaleDateString("id-ID", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        });
        const h = String(dt.getHours()).padStart(2, "0");
        const m = String(dt.getMinutes()).padStart(2, "0");
        return `${date}, ${h}:${m}`;
      };

      let r = this.INIT;

      // === HEADER ===
      r += this.ALIGN_CENTER;
      r += this.BOLD_ON + storeName + this.LINE_FEED + this.BOLD_OFF;
      if (storeAddress) r += storeAddress + this.LINE_FEED;
      if (storePhone) r += "Telp: " + storePhone + this.LINE_FEED;
      r += "================================" + this.LINE_FEED;

      // === TRANSACTION INFO ===
      r += this.ALIGN_LEFT;
      r += "No: " + (txData.invoice_number || txData.id) + this.LINE_FEED;
      r += "Tgl: " + fmtDate(txData.created_at) + this.LINE_FEED;
      if (txData.cashier_name)
        r += "Kasir: " + txData.cashier_name + this.LINE_FEED;
      if (txData.customer_name)
        r += "Customer: " + txData.customer_name + this.LINE_FEED;
      if (txData.total_pcs != null)
        r += "Jumlah: " + txData.total_pcs + " pcs" + this.LINE_FEED;
      if (txData.clothing_notes)
        r += "Catatan: " + txData.clothing_notes + this.LINE_FEED;
      if (txData.plate_number || txData.vehicle_type) {
        const vehicleInfo = [txData.plate_number, txData.vehicle_type]
          .filter(Boolean)
          .join(" - ");
        r += "Kendaraan: " + vehicleInfo + this.LINE_FEED;
      }
      r += "--------------------------------" + this.LINE_FEED;

      // === ITEMS ===
      const details = txData.details || [];
      const services = details.filter((d) => d.item_type === "service");
      const products = details.filter((d) => d.item_type === "product");

      if (services.length > 0) {
        r += "[LAYANAN]" + this.LINE_FEED;
        services.forEach((item) => {
          const nameLines = this._wrap(item.item_name || "", 32);
          nameLines.forEach((line) => {
            r += line + this.LINE_FEED;
          });
          const priceLine = `${item.quantity} x ${fmtNum(item.unit_price)}`;
          r +=
            this._cols("  " + priceLine, fmtNum(item.subtotal)) +
            this.LINE_FEED;
        });
      }

      if (products.length > 0) {
        r += "[PRODUK]" + this.LINE_FEED;
        products.forEach((item) => {
          const nameLines = this._wrap(item.item_name || "", 32);
          nameLines.forEach((line) => {
            r += line + this.LINE_FEED;
          });
          const priceLine = `${item.quantity} x ${fmtNum(item.unit_price)}`;
          r +=
            this._cols("  " + priceLine, fmtNum(item.subtotal)) +
            this.LINE_FEED;
        });
      }

      r += "--------------------------------" + this.LINE_FEED;

      // === TOTALS ===
      const subtotalAmt = details.reduce(
        (s, d) => s + Number(d.subtotal || 0),
        0,
      );
      const manualDisc = Number(txData.manual_discount || 0);
      const voucherDisc = Number(txData.voucher_discount || 0);
      const totalDisc = Number(txData.discount_amount || 0);
      const hasDiscount = totalDisc > 0;

      if (hasDiscount) {
        r += this._cols("Subtotal", fmtNum(subtotalAmt)) + this.LINE_FEED;
        if (manualDisc > 0)
          r += this._cols("Diskon", "- " + fmtNum(manualDisc)) + this.LINE_FEED;
        if (voucherDisc > 0) {
          const vLabel = txData.voucher_code
            ? "Voucher (" + txData.voucher_code + ")"
            : "Voucher";
          r += this._cols(vLabel, "- " + fmtNum(voucherDisc)) + this.LINE_FEED;
        }
        r += "--------------------------------" + this.LINE_FEED;
      }

      r += this.BOLD_ON;
      r += this._cols("TOTAL", fmtNum(txData.total_amount)) + this.LINE_FEED;
      r += this.BOLD_OFF;

      // === PAYMENT ===
      const methodRaw = txData.payment_method || "";
      const metodeLabelMap = {
        cash: "Tunai",
        transfer: "Transfer",
        debit: "Kartu Debit",
        kredit: "Kartu Kredit",
        credit: "Kartu Kredit",
        qris: "QRIS",
        unpaid: "BELUM DIBAYAR",
        partial: "DP / Bayar Sebagian",
        split: "Split Payment",
      };
      const method =
        metodeLabelMap[methodRaw.toLowerCase()] || methodRaw.toUpperCase();
      r += this._cols("Pembayaran", method) + this.LINE_FEED;
      if (txData.payment_method === "partial") {
        const dpAmt = Number(txData.dp_amount || 0);
        const dpMet = (txData.dp_payment_method || "").toUpperCase();
        const sisa = Number(txData.total_amount) - dpAmt;
        r +=
          this._cols("DP " + (dpMet ? "(" + dpMet + ")" : ""), fmtNum(dpAmt)) +
          this.LINE_FEED;
        r += this._cols("Sisa Pelunasan", fmtNum(sisa)) + this.LINE_FEED;
      } else if (txData.payment_method === "split") {
        let splits = txData.split_payments || [];
        if (typeof splits === "string") {
          try {
            splits = JSON.parse(splits);
          } catch {
            splits = [];
          }
        }
        for (const sp of splits) {
          r +=
            this._cols(
              (sp.method || "").toUpperCase(),
              fmtNum(Number(sp.amount)),
            ) + this.LINE_FEED;
        }
        if (txData.change_amount > 0) {
          r +=
            this._cols("Kembali", fmtNum(Number(txData.change_amount))) +
            this.LINE_FEED;
        }
      } else if (txData.payment_method === "cash") {
        const paid = Number(txData.paid_amount || txData.total_amount);
        const change = paid - Number(txData.total_amount);
        r += this._cols("Dibayar", fmtNum(paid)) + this.LINE_FEED;
        r +=
          this._cols("Kembali", fmtNum(Math.max(0, change))) + this.LINE_FEED;
      }

      // === FOOTER ===
      r += "--------------------------------" + this.LINE_FEED;
      r += this.ALIGN_CENTER;
      r += footer + this.LINE_FEED;
      r += this.LINE_FEED + this.LINE_FEED;
      r += this.CUT_PAPER;

      await this.sendData(r);
      return { success: true };
    } catch (error) {
      console.error("Print error:", error);
      return { success: false, error: error.message };
    }
  }
}

const bluetoothPrinter = new BluetoothPrinter();
export default bluetoothPrinter;
